import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import { toast } from "sonner";
import { LuBot, LuArrowLeft, LuSave, LuSparkles, LuCalendar, LuUsers, LuMessageSquare, LuPlus, LuSend, LuUser, LuZap, LuPlay, LuTrash2, LuCircleCheck, LuCircleX, LuClock, LuTableProperties, LuBellRing, LuTriangleAlert, LuPencil } from "react-icons/lu";
import { FaWhatsapp } from "react-icons/fa";
import { RiOpenaiFill } from "react-icons/ri";
import { API } from "../App";

const BOOKING_WEEKDAYS = [
  { key: "mon", label: "Lunes" },
  { key: "tue", label: "Martes" },
  { key: "wed", label: "Miércoles" },
  { key: "thu", label: "Jueves" },
  { key: "fri", label: "Viernes" },
  { key: "sat", label: "Sábado" },
  { key: "sun", label: "Domingo" },
];

const DEFAULT_BOOKING_HOURS = {
  enabled: false,
  min_notice_hours: 24,
  days: { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] }
};

const normalizeBookingHours = (bh) => {
  if (!bh) return { ...DEFAULT_BOOKING_HOURS, days: { ...DEFAULT_BOOKING_HOURS.days } };
  const days = { ...DEFAULT_BOOKING_HOURS.days, ...(bh.days || {}) };
  return { enabled: !!bh.enabled, min_notice_hours: bh.min_notice_hours ?? 24, days };
};

const AgentEditorPage = ({ auth }) => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!agentId;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [models, setModels] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    system_prompt: `Eres un asistente virtual profesional y amable. Tu objetivo es ayudar a los clientes respondiendo sus preguntas de manera clara y concisa.

Directrices:
- Sé cortés y profesional en todo momento
- Responde de forma clara y directa
- Si no conoces la respuesta, indica que consultarás con el equipo
- Ofrece ayuda adicional al final de cada respuesta`,
    ai_model: "gpt-5.6-terra",
    classifier_model: "",
    ai_provider: "openai",
    whatsapp_phone: "",
    calendar_enabled: false,
    calendar_connection_id: "",
    calendar_id: "primary",
    calendar_name: "Calendario Principal",
    calendar_account: "",
    booking_hours: normalizeBookingHours(null),
    welcome_message: "¡Hola! Soy tu asistente virtual. ¿En qué puedo ayudarte hoy?",
    widget_type: "chat",
    user_id: auth.user?.id || "",
    collaborator_ids: []
  });
  const [connections, setConnections] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [users, setUsers] = useState([]); // For admin to assign agent
  const [previewMessages, setPreviewMessages] = useState([]);
  const [previewInput, setPreviewInput] = useState("");
  const [previewSending, setPreviewSending] = useState(false);
  const [triggers, setTriggers] = useState([]);
  const [showTriggerForm, setShowTriggerForm] = useState(false);
  const [triggerSaving, setTriggerSaving] = useState(false);
  const [editingTriggerId, setEditingTriggerId] = useState(null);
  const [collaborators, setCollaborators] = useState([]); // {user_id, email, name}
  const [collabEmail, setCollabEmail] = useState("");
  const [collabAdding, setCollabAdding] = useState(false);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const emptyTriggerForm = {
    name: "", type: "google_sheets_leads", interval_minutes: 30, is_active: true,
    config: {
      connection_id: "", sheet_url: "", sheet_name: "Sheet1",
      phone_column: "B", name_column: "A", extra_columns: "", status_column: "", start_row: 2,
      initial_prompt: "Eres un asistente comercial. Escribe un mensaje de bienvenida personalizado y breve para este lead recién llegado de Meta Ads.",
      calendar_id: "primary", hours_before: 24,
      reminder_prompt: "Escribe un recordatorio de cita amable y breve. Incluye fecha, hora y nombre del evento.",
      wait_days: 3, max_attempts: 2,
      reengagement_prompt: "Eres un asistente comercial. El cliente no ha respondido en varios días. Escribe un mensaje de reactivación breve, amable y no invasivo que invite a retomar la conversación.",
      template_name: "", language_code: "es", template_param_columns: "", reengagement_template_params: "",
      salesperson_phone: "", qualification_criteria: "El cliente ha mostrado interés claro en comprar, ha pedido precio, ha pedido más información del producto o ha pedido contacto con un humano.",
      notification_template: "Nuevo lead cualificado:\nCliente: {phone}\nResumen: {summary}"
    }
  };
  const [triggerForm, setTriggerForm] = useState(emptyTriggerForm);

  useEffect(() => {
    fetchModels();
    fetchWhatsappTemplates();
    if (isEditing) {
      fetchAgent();
      fetchTriggers();
    }
    fetchConnections();
    if (auth.user?.role === "admin") {
      fetchUsers();
    }
  }, [agentId, auth.user]);

  const fetchWhatsappTemplates = async () => {
    try {
      const res = await fetch(`${API}/whatsapp/templates`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (res.ok) setWhatsappTemplates(await res.json());
    } catch {}
  };

  const fetchTriggers = async () => {
    if (!agentId) return;
    try {
      const res = await fetch(`${API}/agents/${agentId}/triggers`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (res.ok) setTriggers(await res.json());
    } catch {}
  };

  const handleDeleteTrigger = async (tid) => {
    if (!window.confirm("¿Eliminar esta automatización?")) return;
    await fetch(`${API}/agents/${agentId}/triggers/${tid}`, {
      method: "DELETE", headers: { Authorization: `Bearer ${auth.token}` }
    });
    fetchTriggers();
  };

  const handleRunTrigger = async (tid) => {
    const res = await fetch(`${API}/agents/${agentId}/triggers/${tid}/run`, {
      method: "POST", headers: { Authorization: `Bearer ${auth.token}` }
    });
    if (res.ok) {
      const { toast: t } = await import("sonner");
      t.success("Automatización ejecutándose...");
      setTimeout(fetchTriggers, 5000);
    }
  };

  const handleToggleTrigger = async (trigger) => {
    await fetch(`${API}/agents/${agentId}/triggers/${trigger.id}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ ...trigger, is_active: !trigger.is_active })
    });
    fetchTriggers();
  };

  const handleEditTrigger = (trigger) => {
    const c = trigger.config || {};
    setTriggerForm({
      name: trigger.name,
      type: trigger.type,
      interval_minutes: trigger.interval_minutes,
      is_active: trigger.is_active,
      config: {
        connection_id: c.connection_id || "",
        sheet_url: c.sheet_url || "",
        sheet_name: c.sheet_name || "Sheet1",
        phone_column: c.phone_column || "B",
        name_column: c.name_column || "A",
        extra_columns: c.extra_columns || "",
        status_column: c.status_column || "",
        start_row: c.start_row || 2,
        initial_prompt: c.initial_prompt || "",
        calendar_id: c.calendar_id || "primary",
        hours_before: c.hours_before || 24,
        reminder_prompt: c.reminder_prompt || "",
        wait_days: c.wait_days || 3,
        max_attempts: c.max_attempts || 2,
        reengagement_prompt: c.reengagement_prompt || "",
        template_name: c.template_name || "",
        language_code: c.language_code || "es",
        template_param_columns: c.template_param_columns || "",
        reengagement_template_params: c.reengagement_template_params || "",
        salesperson_phone: c.salesperson_phone || "",
        qualification_criteria: c.qualification_criteria || "",
        notification_template: c.notification_template || ""
      }
    });
    setEditingTriggerId(trigger.id);
    setShowTriggerForm(true);
  };

  const GOOGLE_TYPES = ["google_sheets_leads", "calendar_reminders"];

  const handleSaveTrigger = async () => {
    if (!triggerForm.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (GOOGLE_TYPES.includes(triggerForm.type) && !triggerForm.config.connection_id) {
      toast.error("Selecciona una cuenta de Google"); return;
    }
    if (triggerForm.type === "google_sheets_leads" && !triggerForm.config.sheet_url.trim()) {
      toast.error("La URL de la hoja es obligatoria"); return;
    }
    if (triggerForm.type === "notify_salesperson" && !triggerForm.config.salesperson_phone.trim()) {
      toast.error("El teléfono del vendedor es obligatorio"); return;
    }
    setTriggerSaving(true);
    const isEdit = !!editingTriggerId;
    const url = isEdit
      ? `${API}/agents/${agentId}/triggers/${editingTriggerId}`
      : `${API}/agents/${agentId}/triggers`;
    try {
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
        body: JSON.stringify(triggerForm)
      });
      if (res.ok) {
        toast.success(isEdit ? "Automatización actualizada" : "Automatización creada");
        setShowTriggerForm(false);
        setEditingTriggerId(null);
        setTriggerForm(emptyTriggerForm);
        fetchTriggers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error al guardar automatización");
      }
    } catch {
      toast.error("Error de conexión");
    }
    setTriggerSaving(false);
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // Fetch calendars when connection changes
  useEffect(() => {
    if (formData.calendar_connection_id) {
      fetchCalendars(formData.calendar_connection_id);
    } else {
      setCalendars([]);
    }
  }, [formData.calendar_connection_id]);

  const fetchModels = async () => {
    try {
      const response = await fetch(`${API}/models`);
      const data = await response.json();
      setModels(data.models || []);
    } catch (error) {
      console.error("Error fetching models:", error);
    }
  };

  const fetchConnections = async () => {
    try {
      // Con agentId, el backend devuelve tambien las cuentas conectadas por
      // colaboradores de ESE agente (no solo las propias), para que el dueño
      // pueda elegir el calendario que ya vinculó un colaborador, y viceversa.
      const url = agentId ? `${API}/calendar/connections?agent_id=${agentId}` : `${API}/calendar/connections`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConnections(data);
      }
    } catch (error) {
      console.error("Error fetching connections:", error);
    }
  };

  const fetchCalendars = async (connId) => {
    try {
      const response = await fetch(`${API}/calendar/list?connection_id=${connId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCalendars(data);
      }
    } catch (error) {
      console.error("Error fetching calendars:", error);
    }
  };

  const handleAddCollaborator = async (e) => {
    e.preventDefault();
    if (!collabEmail.trim()) return;
    setCollabAdding(true);
    try {
      const res = await fetch(`${API}/agents/${agentId}/collaborators`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ email: collabEmail.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setCollaborators(prev => [...prev, { user_id: data.user_id, email: data.email, name: data.name }]);
        setCollabEmail("");
      } else {
        toast.error(data.detail || "Error al añadir colaborador");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setCollabAdding(false);
    }
  };

  const handleRemoveCollaborator = async (userId) => {
    try {
      const res = await fetch(`${API}/agents/${agentId}/collaborators/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (res.ok) {
        setCollaborators(prev => prev.filter(c => c.user_id !== userId));
        toast.success("Colaborador eliminado");
      }
    } catch {
      toast.error("Error de conexión");
    }
  };

  const fetchAgent = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/agents/${agentId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.ok) {
        const agent = await response.json();
        setFormData({
          name: agent.name,
          description: agent.description || "",
          system_prompt: agent.system_prompt,
          ai_model: agent.ai_model,
          classifier_model: agent.classifier_model || "",
          ai_provider: agent.ai_provider,
          whatsapp_phone: agent.whatsapp_phone || "",
          calendar_enabled: agent.calendar_enabled,
          calendar_connection_id: agent.calendar_connection_id || "",
          calendar_id: agent.calendar_id || "primary",
          calendar_name: agent.calendar_name || "Calendario Principal",
          calendar_account: agent.calendar_account || "",
          booking_hours: normalizeBookingHours(agent.booking_hours),
          welcome_message: agent.welcome_message || "",
          widget_type: agent.widget_type || "chat",
          user_id: agent.user_id || auth.user?.id || "",
          collaborator_ids: agent.collaborator_ids || []
        });
        // Build collaborators list from collaborator_ids + emails
        if (agent.collaborator_ids?.length) {
          const collabs = agent.collaborator_ids.map((uid, i) => ({
            user_id: uid,
            email: agent.collaborator_emails?.[i] || uid,
            name: agent.collaborator_emails?.[i] || uid
          }));
          setCollaborators(collabs);
        }
      } else {
        toast.error("Agente no encontrado");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error("Error al cargar agente");
    } finally {
      setLoading(false);
    }
  };

  const handleBookingWindowChange = (dayKey, windowIndex, side, value) => {
    setFormData(f => {
      const existing = f.booking_hours.days[dayKey] || [];
      const padded = [existing[0] || ["", ""], existing[1] || ["", ""]];
      padded[windowIndex] = [...padded[windowIndex]];
      padded[windowIndex][side] = value;
      return {
        ...f,
        booking_hours: { ...f.booking_hours, days: { ...f.booking_hours.days, [dayKey]: padded } }
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = isEditing ? `${API}/agents/${agentId}` : `${API}/agents`;
      const method = isEditing ? "PUT" : "POST";

      // Solo mandamos franjas completas (inicio y fin rellenos); descartamos las que
      // el usuario dejó a medias mientras editaba.
      const payload = {
        ...formData,
        booking_hours: {
          ...formData.booking_hours,
          days: Object.fromEntries(
            Object.entries(formData.booking_hours.days).map(([k, windows]) => [k, (windows || []).filter(w => w[0] && w[1])])
          )
        }
      };

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(isEditing ? "Agente actualizado" : "Agente creado exitosamente");
        navigate("/dashboard");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Error al guardar agente");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const handleModelChange = (modelId) => {
    const selectedModel = models.find(m => m.model === modelId);
    if (selectedModel) {
      setFormData({
        ...formData,
        ai_model: selectedModel.model,
        ai_provider: selectedModel.provider
      });
    }
  };

  const handlePreviewSend = async (e) => {
    e.preventDefault();
    if (!previewInput.trim() || previewSending) return;

    const userMsg = { role: "user", content: previewInput.trim() };
    setPreviewMessages(prev => [...prev, userMsg]);
    setPreviewInput("");
    setPreviewSending(true);

    try {
      const response = await fetch(`${API}/chat`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          agent_id: agentId || "new_preview",
          message: userMsg.content,
          phone_number: "preview",
          system_prompt_override: formData.system_prompt
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewMessages(prev => [...prev, { role: "assistant", content: data.response }]);
      } else {
        toast.error("Error en la vista previa");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setPreviewSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="rounded-xl" data-testid="back-btn">
                <LuArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl gradient-hero flex items-center justify-center">
                <LuBot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg text-foreground">
                  {isEditing ? "Editar Agente" : "Nuevo Agente"}
                </h1>
                <p className="text-xs text-muted-foreground">
                  {isEditing ? "Modifica la configuración" : "Configura tu agente de WhatsApp"}
                </p>
              </div>
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            className="rounded-full bg-primary hover:bg-primary/90 glow-primary"
            disabled={saving}
            data-testid="save-agent-btn"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
            ) : (
              <>
                <LuSave className="w-5 h-5 mr-2" />
                Guardar
              </>
            )}
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Editor Form */}
          <div className="lg:col-span-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Basic Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="glass-card border-white/10">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-foreground">
                      <LuBot className="w-5 h-5 text-primary" />
                      Información Básica
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-foreground">Nombre del Agente</Label>
                      <Input
                        id="name"
                        placeholder="Ej: Asistente de Ventas"
                        className="bg-muted/50 border-transparent focus:border-primary h-12 rounded-xl"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="agent-name-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        El número de WhatsApp se asigna desde{" "}
                        <a href="/phone-assignments" className="text-primary underline">Asignaciones</a>.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description" className="text-foreground">Descripción</Label>
                      <Input
                        id="description"
                        placeholder="Describe brevemente el propósito de este agente"
                        className="bg-muted/50 border-transparent focus:border-primary h-12 rounded-xl"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        data-testid="agent-description-input"
                      />
                    </div>

                    {auth.user?.role === "admin" && (
                      <div className="space-y-6 mt-4 pt-4 border-t border-white/5">
                        <div className="space-y-2">
                          <Label className="text-foreground font-medium flex items-center gap-2">
                            <LuUsers className="w-4 h-4 text-primary" />
                            Dueño Principal (Admin)
                          </Label>
                          <select
                            className="w-full h-12 bg-muted/50 border-transparent focus:border-primary rounded-xl px-4 text-sm outline-none appearance-none text-foreground"
                            value={formData.user_id}
                            onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                          >
                            {users.map((user) => (
                              <option key={user.id} value={user.id} className="bg-background text-foreground">
                                {user.email} ({user.name})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-3">
                          <Label className="text-foreground font-medium flex items-center gap-2">
                            <LuPlus className="w-4 h-4 text-primary" />
                            Otros Usuarios con Acceso (Admin)
                          </Label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-2 scrollbar-thin">
                            {users.filter(u => u.id !== formData.user_id).map((user) => (
                              <label
                                key={user.id}
                                className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${formData.collaborator_ids.includes(user.id)
                                  ? "bg-primary/20 border-primary/40"
                                  : "bg-muted/30 border-white/5 hover:bg-muted/50"
                                  }`}
                              >
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 rounded border-white/20 accent-primary"
                                  checked={formData.collaborator_ids.includes(user.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        collaborator_ids: [...formData.collaborator_ids, user.id]
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        collaborator_ids: formData.collaborator_ids.filter(id => id !== user.id)
                                      });
                                    }
                                  }}
                                />
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-foreground truncate">{user.name}</p>
                                  <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                          <p className="text-[10px] text-muted-foreground italic">
                            Los usuarios seleccionados podrán ver y gestionar este agente desde su propio dashboard.
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

              {/* Collaborators */}
              {isEditing && (auth.user?.role === "admin" || formData.user_id === auth.user?.id) && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
                  <Card className="glass-card border-white/10">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-foreground">
                        <LuUsers className="w-5 h-5 text-primary" />
                        Usuarios con acceso
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Los usuarios añadidos aquí verán las conversaciones, leads, calendarios y configuración de este agente.
                      </p>

                      {/* Add collaborator */}
                      <form onSubmit={handleAddCollaborator} className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="email@usuario.com"
                          className="bg-muted/50 border-transparent focus:border-primary rounded-xl"
                          value={collabEmail}
                          onChange={e => setCollabEmail(e.target.value)}
                        />
                        <Button type="submit" className="rounded-xl shrink-0" disabled={collabAdding || !collabEmail.trim()}>
                          {collabAdding ? "Añadiendo..." : "Añadir"}
                        </Button>
                      </form>

                      {/* Collaborators list */}
                      {collaborators.length === 0 ? (
                        <p className="text-sm text-muted-foreground italic text-center py-3">Sin usuarios asignados</p>
                      ) : (
                        <div className="space-y-2">
                          {collaborators.map(c => (
                            <div key={c.user_id} className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-white/5">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">
                                  {(c.name || c.email).charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-foreground">{c.name || c.email}</p>
                                  {c.name && c.name !== c.email && <p className="text-xs text-muted-foreground">{c.email}</p>}
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground hover:text-destructive"
                                onClick={() => handleRemoveCollaborator(c.user_id)}
                              >
                                <LuTrash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* AI Configuration */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card className="glass-card border-white/10">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-foreground">
                      <RiOpenaiFill className="w-5 h-5 text-primary" />
                      Configuración de IA
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {auth.user?.role === "admin" ? (
                      <>
                        <div className="space-y-2">
                          <Label className="text-foreground">Modelo de IA (conversación)</Label>
                          <Select
                            value={formData.ai_model}
                            onValueChange={handleModelChange}
                          >
                            <SelectTrigger className="bg-muted/50 border-transparent focus:border-primary h-12 rounded-xl" data-testid="ai-model-select">
                              <SelectValue placeholder="Selecciona un modelo" />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              {models.map((model) => (
                                <SelectItem key={model.model} value={model.model}>
                                  <div className="flex items-center gap-2">
                                    <span className="capitalize text-xs text-muted-foreground">[{model.provider}]</span>
                                    {model.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Proveedor: <span className="capitalize">{formData.ai_provider}</span>
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-foreground">Modelo de clasificación (descualificación de leads)</Label>
                          <Select
                            value={formData.classifier_model || "__default__"}
                            onValueChange={(modelId) => setFormData({
                              ...formData,
                              classifier_model: modelId === "__default__" ? "" : modelId
                            })}
                          >
                            <SelectTrigger className="bg-muted/50 border-transparent focus:border-primary h-12 rounded-xl" data-testid="classifier-model-select">
                              <SelectValue placeholder="Selecciona un modelo" />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="__default__">Usar el modelo global de la plataforma</SelectItem>
                              {models.map((model) => (
                                <SelectItem key={model.model} value={model.model}>
                                  <div className="flex items-center gap-2">
                                    <span className="capitalize text-xs text-muted-foreground">[{model.provider}]</span>
                                    {model.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Modelo barato usado para detectar leads descualificados antes de llamar al modelo de conversación.
                          </p>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-1 p-3 rounded-xl bg-muted/30">
                        <Label className="text-foreground">Modelo de IA</Label>
                        <p className="text-xs text-muted-foreground">
                          Gestionado por el administrador de la plataforma.
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="welcome" className="text-foreground">Mensaje de Bienvenida</Label>
                      <Textarea
                        id="welcome"
                        placeholder="El primer mensaje que enviará el bot"
                        className="bg-muted/50 border-transparent focus:border-primary rounded-xl min-h-[80px]"
                        value={formData.welcome_message}
                        onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
                        data-testid="welcome-message-input"
                      />
                    </div>

                    <div className="space-y-4 pt-4 border-t border-white/5">
                      <Label className="text-foreground font-medium">Tipo de Widget</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, widget_type: "chat" })}
                          className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${formData.widget_type === "chat"
                            ? "border-primary bg-primary/10"
                            : "border-white/5 bg-muted/30 hover:border-white/20"
                            }`}
                        >
                          <LuMessageSquare className={`w-6 h-6 ${formData.widget_type === "chat" ? "text-primary" : "text-muted-foreground"}`} />
                          <div className="text-center">
                            <p className={`text-sm font-bold ${formData.widget_type === "chat" ? "text-foreground" : "text-muted-foreground"}`}>Web Chat</p>
                            <p className="text-[10px] text-muted-foreground">IA responde en la web</p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, widget_type: "whatsapp" })}
                          className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${formData.widget_type === "whatsapp"
                            ? "border-green-500 bg-green-500/10"
                            : "border-white/5 bg-muted/30 hover:border-white/20"
                            }`}
                        >
                          <FaWhatsapp className={`w-6 h-6 ${formData.widget_type === "whatsapp" ? "text-green-500" : "text-muted-foreground"}`} />
                          <div className="text-center">
                            <p className={`text-sm font-bold ${formData.widget_type === "whatsapp" ? "text-foreground" : "text-muted-foreground"}`}>Directo WhatsApp</p>
                            <p className="text-[10px] text-muted-foreground">Abre WhatsApp al clic</p>
                          </div>
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* System Prompt */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="glass-card border-white/10">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-foreground">
                      <LuSparkles className="w-5 h-5 text-primary" />
                      Prompt del Sistema
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="prompt" className="text-foreground">
                        Define la personalidad y comportamiento del agente
                      </Label>
                      <Textarea
                        id="prompt"
                        placeholder="Escribe las instrucciones para tu agente..."
                        className="bg-muted/50 border-transparent focus:border-primary rounded-xl min-h-[250px] font-mono text-sm"
                        value={formData.system_prompt}
                        onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                        required
                        data-testid="system-prompt-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Este prompt define cómo se comportará el agente. Sé específico sobre el tono, las respuestas y las limitaciones.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Integrations */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="glass-card border-white/10">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-foreground">
                      <LuCalendar className="w-5 h-5 text-primary" />
                      Integraciones
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <LuCalendar className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">Google Calendar (Configurador)</p>
                            <p className="text-sm text-muted-foreground">
                              Asocia este agente a un calendario específico
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={formData.calendar_enabled}
                          onCheckedChange={(checked) => setFormData({ ...formData, calendar_enabled: checked })}
                          disabled={!auth.user?.google_calendar_connected}
                          data-testid="calendar-switch"
                        />
                      </div>

                      {formData.calendar_enabled && (
                        <motion.div
                          className="space-y-4 p-4 rounded-xl bg-primary/5 border border-primary/10"
                        >
                          <div className="space-y-2">
                            <Label className="text-foreground text-sm font-medium">Cuenta de Google</Label>
                            <Select
                              value={formData.calendar_connection_id}
                              onValueChange={(val) => {
                                const conn = connections.find(c => c.id === val);
                                setFormData({
                                  ...formData,
                                  calendar_connection_id: val,
                                  calendar_account: conn ? conn.email : "",
                                  calendar_id: "primary",
                                  calendar_name: "Calendario Principal"
                                });
                              }}
                            >
                              <SelectTrigger className="bg-background border-white/10 h-10 rounded-lg">
                                <SelectValue placeholder="Selecciona una cuenta" />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                {connections.map((conn) => (
                                  <SelectItem key={conn.id} value={conn.id}>
                                    {conn.email}
                                  </SelectItem>
                                ))}
                                <SelectItem value="primary_legacy">
                                  (Cuenta principal del perfil)
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {formData.calendar_connection_id && (
                            <div className="space-y-2">
                              <Label className="text-foreground text-sm font-medium">Seleccionar Calendario</Label>
                              <Select
                                value={formData.calendar_id}
                                onValueChange={(val) => {
                                  const cal = calendars.find(c => c.id === val);
                                  setFormData({
                                    ...formData,
                                    calendar_id: val,
                                    calendar_name: cal ? cal.summary : (val === "primary" ? "Calendario Principal" : val)
                                  });
                                }}
                              >
                                <SelectTrigger className="bg-background border-white/10 h-10 rounded-lg">
                                  <SelectValue placeholder={calendars.length > 0 ? "Selecciona un calendario" : "Cargando calendarios..."} />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                  {calendars.length > 0 ? (
                                    calendars.map((cal) => (
                                      <SelectItem key={cal.id} value={cal.id}>
                                        {cal.summary} {cal.primary && "(Principal)"}
                                      </SelectItem>
                                    ))
                                  ) : (
                                    <div className="p-2 text-xs text-muted-foreground text-center">No hay calendarios disponibles</div>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {formData.calendar_account && (
                            <div className="flex flex-col gap-1 p-3 rounded-xl bg-green-500/10 border border-green-500/20">
                              <div className="flex items-center gap-2">
                                <LuCalendar className="w-4 h-4 text-green-400" />
                                <span className="text-[10px] text-green-400 font-bold uppercase tracking-wider">Configuración Activa</span>
                              </div>
                              <p className="text-[11px] text-green-400/80 leading-tight">
                                Este agente agendará en <span className="font-bold underline">{formData.calendar_name}</span> de <span className="font-bold">{formData.calendar_account}</span>
                              </p>
                            </div>
                          )}

                          <p className="text-[10px] text-muted-foreground">
                            Las citas se agendarán únicamente en la cuenta y calendario seleccionados.
                          </p>

                          {/* Horario de reservas */}
                          <div className="pt-4 mt-1 border-t border-white/10 space-y-3">
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <Label className="text-foreground text-sm font-medium">Horario de reservas</Label>
                                <p className="text-[11px] text-muted-foreground">Restringe los días y horas en los que el agente puede agendar citas.</p>
                              </div>
                              <Switch
                                checked={formData.booking_hours.enabled}
                                onCheckedChange={(checked) => setFormData({ ...formData, booking_hours: { ...formData.booking_hours, enabled: checked } })}
                              />
                            </div>

                            {formData.booking_hours.enabled && (
                              <div className="space-y-3 p-3 rounded-xl bg-background/40 border border-white/10">
                                <div className="flex items-center gap-2">
                                  <Label className="text-xs text-muted-foreground whitespace-nowrap">Antelación mínima (horas)</Label>
                                  <Input type="number" min={0} className="h-8 w-20 bg-background border-white/10 text-sm"
                                    value={formData.booking_hours.min_notice_hours}
                                    onChange={e => setFormData({ ...formData, booking_hours: { ...formData.booking_hours, min_notice_hours: Number(e.target.value) } })} />
                                </div>

                                <div className="space-y-1.5">
                                  {BOOKING_WEEKDAYS.map(({ key, label }) => {
                                    const windows = formData.booking_hours.days[key] || [];
                                    const w1 = windows[0] || ["", ""];
                                    const w2 = windows[1] || ["", ""];
                                    const isClosed = !w1[0] && !w1[1] && !w2[0] && !w2[1];
                                    return (
                                      <div key={key} className="flex items-center gap-2 text-xs flex-wrap">
                                        <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
                                        <input type="time" className="bg-background border border-white/10 rounded-lg px-2 py-1 text-xs w-[90px]"
                                          value={w1[0]} onChange={e => handleBookingWindowChange(key, 0, 0, e.target.value)} />
                                        <span className="text-muted-foreground">–</span>
                                        <input type="time" className="bg-background border border-white/10 rounded-lg px-2 py-1 text-xs w-[90px]"
                                          value={w1[1]} onChange={e => handleBookingWindowChange(key, 0, 1, e.target.value)} />
                                        <span className="text-muted-foreground mx-1">y</span>
                                        <input type="time" className="bg-background border border-white/10 rounded-lg px-2 py-1 text-xs w-[90px]"
                                          value={w2[0]} onChange={e => handleBookingWindowChange(key, 1, 0, e.target.value)} />
                                        <span className="text-muted-foreground">–</span>
                                        <input type="time" className="bg-background border border-white/10 rounded-lg px-2 py-1 text-xs w-[90px]"
                                          value={w2[1]} onChange={e => handleBookingWindowChange(key, 1, 1, e.target.value)} />
                                        {isClosed && <span className="text-[10px] text-muted-foreground italic">Cerrado</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                                <p className="text-[10px] text-muted-foreground">
                                  Hasta dos franjas por día (p. ej. mañana y tarde). Deja ambas vacías para cerrar ese día por completo (incluido fin de semana).
                                </p>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </div>
                    {!auth.user?.google_calendar_connected && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Conecta tu Google Calendar desde el Dashboard para habilitar esta función
                      </p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

              {/* Automatizaciones — solo visible al editar un agente existente */}
              {isEditing && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
                  <Card className="glass-card border-white/10">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2 text-foreground">
                          <LuBellRing className="w-5 h-5 text-primary" />
                          Automatizaciones
                        </CardTitle>
                        <button type="button" onClick={() => {
                          setTriggerForm(emptyTriggerForm);
                          setEditingTriggerId(null);
                          setShowTriggerForm(true);
                        }}
                          className="flex items-center gap-1.5 text-xs text-primary hover:underline">
                          <LuPlus className="w-3.5 h-3.5" />Nueva
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Acciones proactivas: contactar leads de Google Sheets, enviar recordatorios de calendario, etc.
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-4">

                      {/* Lista de triggers existentes */}
                      {triggers.length === 0 && !showTriggerForm && (
                        <p className="text-xs text-muted-foreground text-center py-4">
                          No hay automatizaciones. Crea una para que el agente actúe de forma proactiva.
                        </p>
                      )}
                      {triggers.map(tr => (
                        <div key={tr.id} className={`p-4 rounded-xl border ${tr.is_active ? 'bg-primary/5 border-primary/20' : 'bg-muted/30 border-white/5'}`}>
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${tr.is_active ? 'bg-primary/20' : 'bg-muted/50'}`}>
                              {tr.type === 'google_sheets_leads' ? <LuTableProperties className="w-4 h-4 text-primary" /> : tr.type === 'reengagement' ? <LuZap className="w-4 h-4 text-primary" /> : tr.type === 'notify_salesperson' ? <LuUser className="w-4 h-4 text-primary" /> : <LuBellRing className="w-4 h-4 text-primary" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-sm text-foreground">{tr.name}</p>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                  {tr.type === 'google_sheets_leads' ? 'Google Sheets' : tr.type === 'reengagement' ? 'Re-engagement' : tr.type === 'notify_salesperson' ? 'Notif. vendedor' : 'Recordatorios'}
                                </span>
                                <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                  <LuClock className="w-2.5 h-2.5" />cada {tr.interval_minutes}min
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {tr.last_run_status === 'never' && <span className="text-[10px] text-muted-foreground">Nunca ejecutada</span>}
                                {tr.last_run_status === 'ok' && <span className="text-[10px] text-green-400 flex items-center gap-0.5"><LuCircleCheck className="w-2.5 h-2.5" />{tr.last_run_message}</span>}
                                {tr.last_run_status === 'error' && <span className="text-[10px] text-red-400 flex items-center gap-0.5"><LuTriangleAlert className="w-2.5 h-2.5" />{tr.last_run_message}</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button type="button" title="Ejecutar ahora" onClick={() => handleRunTrigger(tr.id)}
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                                <LuPlay className="w-3.5 h-3.5" />
                              </button>
                              <button type="button" title="Editar" onClick={() => handleEditTrigger(tr)}
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-amber-400 hover:bg-amber-500/10 transition-colors">
                                <LuPencil className="w-3.5 h-3.5" />
                              </button>
                              <button type="button" title={tr.is_active ? "Pausar" : "Activar"} onClick={() => handleToggleTrigger(tr)}
                                className={`p-1.5 rounded-lg transition-colors ${tr.is_active ? 'text-green-400 hover:bg-green-500/10' : 'text-muted-foreground hover:bg-muted/50'}`}>
                                {tr.is_active ? <LuCircleCheck className="w-3.5 h-3.5" /> : <LuCircleX className="w-3.5 h-3.5" />}
                              </button>
                              <button type="button" title="Eliminar" onClick={() => handleDeleteTrigger(tr.id)}
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                                <LuTrash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Formulario de nueva automatización */}
                      {showTriggerForm && (
                        <div className="space-y-4 p-4 rounded-xl bg-muted/30 border border-white/10">
                          <p className="text-xs font-semibold text-foreground">
                            {editingTriggerId ? "Editar automatización" : "Nueva automatización"}
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label className="text-xs text-foreground">Nombre</Label>
                              <input placeholder="Ej: Leads Meta → WhatsApp" required
                                className="w-full bg-muted/50 border border-transparent rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                                value={triggerForm.name} onChange={e => setTriggerForm({ ...triggerForm, name: e.target.value })} />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs text-foreground">Tipo</Label>
                              <select className="w-full bg-muted/50 border border-transparent rounded-lg px-3 py-2 text-sm text-foreground"
                                value={triggerForm.type} onChange={e => setTriggerForm({ ...triggerForm, type: e.target.value })}>
                                <option value="google_sheets_leads">Google Sheets → Leads</option>
                                <option value="calendar_reminders">Recordatorios de calendario</option>
                                <option value="reengagement">Re-engagement de leads fríos</option>
                                <option value="notify_salesperson">Notificar al vendedor</option>
                              </select>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {['google_sheets_leads', 'calendar_reminders'].includes(triggerForm.type) && (
                              <div className="space-y-1.5">
                                <Label className="text-xs text-foreground">Cuenta de Google</Label>
                                <select className="w-full bg-muted/50 border border-transparent rounded-lg px-3 py-2 text-sm text-foreground"
                                  value={triggerForm.config.connection_id}
                                  onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, connection_id: e.target.value } })} required>
                                  <option value="">Selecciona cuenta...</option>
                                  {connections.map(c => <option key={c.id} value={c.id}>{c.email}</option>)}
                                </select>
                              </div>
                            )}
                            <div className="space-y-1.5">
                              <Label className="text-xs text-foreground">Cada (minutos)</Label>
                              <input type="number" min={5} max={1440}
                                className="w-full bg-muted/50 border border-transparent rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                                value={triggerForm.interval_minutes}
                                onChange={e => setTriggerForm({ ...triggerForm, interval_minutes: Number(e.target.value) })} />
                            </div>
                          </div>

                          {triggerForm.type === 'google_sheets_leads' && (
                            <div className="space-y-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
                              <p className="text-[10px] font-bold text-primary uppercase tracking-wider flex items-center gap-1">
                                <LuTableProperties className="w-3 h-3" />Configuración Google Sheets
                              </p>
                              <div className="space-y-1.5">
                                <Label className="text-xs text-foreground">URL de la hoja</Label>
                                <input placeholder="https://docs.google.com/spreadsheets/d/..." required
                                  className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-foreground focus:outline-none focus:border-primary"
                                  value={triggerForm.config.sheet_url}
                                  onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, sheet_url: e.target.value } })} />
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                                <div className="space-y-1.5">
                                  <Label className="text-xs text-foreground">Pestaña</Label>
                                  <input placeholder="Sheet1"
                                    className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary"
                                    value={triggerForm.config.sheet_name}
                                    onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, sheet_name: e.target.value } })} />
                                </div>
                                <div className="space-y-1.5">
                                  <Label className="text-xs text-foreground">Col. teléfono</Label>
                                  <input placeholder="B"
                                    className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-foreground focus:outline-none focus:border-primary"
                                    value={triggerForm.config.phone_column}
                                    onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, phone_column: e.target.value } })} />
                                </div>
                                <div className="space-y-1.5">
                                  <Label className="text-xs text-foreground">Col. nombre</Label>
                                  <input placeholder="A"
                                    className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-foreground focus:outline-none focus:border-primary"
                                    value={triggerForm.config.name_column}
                                    onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, name_column: e.target.value } })} />
                                </div>
                                <div className="space-y-1.5">
                                  <Label className="text-xs text-foreground">Col. estado</Label>
                                  <input placeholder="F"
                                    className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-foreground focus:outline-none focus:border-primary"
                                    value={triggerForm.config.status_column}
                                    onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, status_column: e.target.value } })} />
                                </div>
                                <div className="space-y-1.5">
                                  <Label className="text-xs text-foreground">Fila inicio</Label>
                                  <input type="number" min={1} placeholder="2"
                                    className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-foreground focus:outline-none focus:border-primary"
                                    value={triggerForm.config.start_row}
                                    onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, start_row: Number(e.target.value) } })} />
                                </div>
                              </div>
                              <p className="text-[10px] text-muted-foreground -mt-1">
                                Col. estado: columna donde el agente escribirá el resumen del lead (deja vacío para no usar). Fila inicio: primera fila con datos (normalmente 2, después del encabezado).
                              </p>
                              <div className="space-y-1.5">
                                <Label className="text-xs text-foreground">Columnas extra (separadas por coma, p.ej: C,D)</Label>
                                <input placeholder="C,D"
                                  className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-foreground focus:outline-none focus:border-primary"
                                  value={triggerForm.config.extra_columns}
                                  onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, extra_columns: e.target.value } })} />
                                <p className="text-[10px] text-muted-foreground">El agente recibirá los datos de estas columnas como contexto adicional del lead</p>
                              </div>
                              <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 space-y-3">
                                <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">Plantilla de Meta (obligatoria para contactos nuevos)</p>
                                <p className="text-[10px] text-muted-foreground">Los leads de una hoja nunca han escrito primero, por lo que siempre estás fuera de la ventana de 24h. Sin plantilla aprobada en Meta el mensaje será rechazado.</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div className="space-y-1.5">
                                    <Label className="text-xs text-foreground">Plantilla de Meta</Label>
                                    {whatsappTemplates.length > 0 ? (
                                      <select
                                        className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary"
                                        value={triggerForm.config.template_name}
                                        onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, template_name: e.target.value } })}>
                                        <option value="">— Sin plantilla —</option>
                                        {whatsappTemplates.map(t => (
                                          <option key={t.id} value={t.template_name}>{t.template_name}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      <input placeholder="nombre_de_plantilla"
                                        className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-foreground focus:outline-none focus:border-primary"
                                        value={triggerForm.config.template_name}
                                        onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, template_name: e.target.value } })} />
                                    )}
                                    <p className="text-[10px] text-muted-foreground">Debe estar aprobada en Meta for Developers</p>
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label className="text-xs text-foreground">Idioma</Label>
                                    <select
                                      className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary"
                                      value={triggerForm.config.language_code}
                                      onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, language_code: e.target.value } })}>
                                      <option value="es">Español (es)</option>
                                      <option value="es_ES">Español España (es_ES)</option>
                                      <option value="en">Inglés (en)</option>
                                      <option value="en_US">Inglés US (en_US)</option>
                                      <option value="pt_BR">Portugués BR (pt_BR)</option>
                                    </select>
                                  </div>
                                </div>
                                {/* Mapeo de parámetros de plantilla */}
                                {(() => {
                                  const selectedTpl = whatsappTemplates.find(t => t.template_name === triggerForm.config.template_name);
                                  if (!selectedTpl || !selectedTpl.parameters_count) return null;
                                  const paramCols = (triggerForm.config.template_param_columns || "").split(",").map(s => s.trim());
                                  return (
                                    <div className="space-y-2 mt-2">
                                      <p className="text-[10px] font-bold text-blue-300 uppercase tracking-wider">Mapeo de parámetros → columnas de Sheets</p>
                                      {selectedTpl.body_text && (
                                        <p className="text-[10px] text-muted-foreground italic border-l-2 border-blue-400/40 pl-2">
                                          {selectedTpl.body_text}
                                        </p>
                                      )}
                                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {Array.from({ length: selectedTpl.parameters_count }, (_, i) => (
                                          <div key={i} className="space-y-1">
                                            <Label className="text-[10px] text-foreground font-mono">{`{{${i + 1}}}`} → Col.</Label>
                                            <input
                                              placeholder={["A","B","C","D"][i] || "?"}
                                              maxLength={3}
                                              className="w-full bg-background border border-blue-400/30 rounded-lg px-2 py-1.5 text-xs font-mono text-foreground focus:outline-none focus:border-blue-400 uppercase"
                                              value={paramCols[i] || ""}
                                              onChange={e => {
                                                const updated = [...Array.from({ length: selectedTpl.parameters_count }, (_, j) => paramCols[j] || "")];
                                                updated[i] = e.target.value.toUpperCase();
                                                setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, template_param_columns: updated.join(",") } });
                                              }}
                                            />
                                          </div>
                                        ))}
                                      </div>
                                      <p className="text-[10px] text-muted-foreground">Indica la letra de la columna de tu hoja que corresponde a cada parámetro de la plantilla.</p>
                                    </div>
                                  );
                                })()}
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-xs text-foreground">Instrucción para el primer mensaje</Label>
                                <textarea rows={2}
                                  className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs text-foreground resize-none focus:outline-none focus:border-primary"
                                  value={triggerForm.config.initial_prompt}
                                  onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, initial_prompt: e.target.value } })} />
                              </div>
                            </div>
                          )}

                          {triggerForm.type === 'calendar_reminders' && (
                            <div className="space-y-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
                              <p className="text-[10px] font-bold text-primary uppercase tracking-wider flex items-center gap-1">
                                <LuBellRing className="w-3 h-3" />Configuración de recordatorios
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                  <Label className="text-xs text-foreground">Calendario</Label>
                                  <select className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs text-foreground"
                                    value={triggerForm.config.calendar_id}
                                    onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, calendar_id: e.target.value } })}>
                                    <option value="primary">Principal</option>
                                    {calendars.map(c => <option key={c.id} value={c.id}>{c.summary}</option>)}
                                  </select>
                                </div>
                                <div className="space-y-1.5">
                                  <Label className="text-xs text-foreground">Horas de antelación</Label>
                                  <input type="number" min={1} max={168}
                                    className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary"
                                    value={triggerForm.config.hours_before}
                                    onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, hours_before: Number(e.target.value) } })} />
                                </div>
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-xs text-foreground">Instrucción para el recordatorio</Label>
                                <textarea rows={2}
                                  className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs text-foreground resize-none focus:outline-none focus:border-primary"
                                  value={triggerForm.config.reminder_prompt}
                                  onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, reminder_prompt: e.target.value } })} />
                              </div>
                              <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                <p className="text-[10px] text-amber-400">
                                  El teléfono se extraerá automáticamente de la descripción del evento (ej: "Teléfono: 34612345678"). Asegúrate de que está en el formato correcto.
                                </p>
                              </div>
                            </div>
                          )}

                          {triggerForm.type === 'reengagement' && (
                            <div className="space-y-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
                              <p className="text-[10px] font-bold text-primary uppercase tracking-wider flex items-center gap-1">
                                <LuZap className="w-3 h-3" />Configuración de re-engagement
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                  <Label className="text-xs text-foreground">Días sin respuesta</Label>
                                  <input type="number" min={1} max={30}
                                    className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary"
                                    value={triggerForm.config.wait_days}
                                    onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, wait_days: Number(e.target.value) } })} />
                                  <p className="text-[10px] text-muted-foreground">Días de inactividad antes de contactar</p>
                                </div>
                                <div className="space-y-1.5">
                                  <Label className="text-xs text-foreground">Máximo de intentos</Label>
                                  <input type="number" min={1} max={5}
                                    className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary"
                                    value={triggerForm.config.max_attempts}
                                    onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, max_attempts: Number(e.target.value) } })} />
                                  <p className="text-[10px] text-muted-foreground">Intentos antes de marcar como perdido</p>
                                </div>
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-xs text-foreground">Instrucción para el mensaje de reactivación</Label>
                                <textarea rows={3}
                                  className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs text-foreground resize-none focus:outline-none focus:border-primary"
                                  value={triggerForm.config.reengagement_prompt}
                                  onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, reengagement_prompt: e.target.value } })} />
                              </div>
                              <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 space-y-3">
                                <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">Plantilla de Meta (obligatoria fuera de ventana 24h)</p>
                                <p className="text-[10px] text-muted-foreground">El re-engagement siempre ocurre fuera de la ventana de 24h de WhatsApp. Sin plantilla aprobada en Meta, el mensaje puede ser rechazado en producción.</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div className="space-y-1.5">
                                    <Label className="text-xs text-foreground">Plantilla de Meta</Label>
                                    {whatsappTemplates.length > 0 ? (
                                      <select
                                        className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary"
                                        value={triggerForm.config.template_name}
                                        onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, template_name: e.target.value } })}>
                                        <option value="">— Sin plantilla —</option>
                                        {whatsappTemplates.map(t => (
                                          <option key={t.id} value={t.template_name}>{t.template_name}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      <input placeholder="nombre_de_plantilla"
                                        className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-foreground focus:outline-none focus:border-primary"
                                        value={triggerForm.config.template_name}
                                        onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, template_name: e.target.value } })} />
                                    )}
                                    <p className="text-[10px] text-muted-foreground">Debe estar aprobada en Meta for Developers</p>
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label className="text-xs text-foreground">Idioma</Label>
                                    <select
                                      className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary"
                                      value={triggerForm.config.language_code}
                                      onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, language_code: e.target.value } })}>
                                      <option value="es">Español (es)</option>
                                      <option value="es_ES">Español España (es_ES)</option>
                                      <option value="en">Inglés (en)</option>
                                      <option value="en_US">Inglés US (en_US)</option>
                                      <option value="pt_BR">Portugués BR (pt_BR)</option>
                                    </select>
                                  </div>
                                </div>
                                {/* Parámetros de plantilla para re-engagement */}
                                {(() => {
                                  const selectedTpl = whatsappTemplates.find(t => t.template_name === triggerForm.config.template_name);
                                  if (!selectedTpl || !selectedTpl.parameters_count) return null;
                                  const paramVals = (triggerForm.config.reengagement_template_params || "").split(",").map(s => s.trim());
                                  return (
                                    <div className="space-y-2 mt-2">
                                      <p className="text-[10px] font-bold text-blue-300 uppercase tracking-wider">Valores de los parámetros</p>
                                      {selectedTpl.body_text && (
                                        <p className="text-[10px] text-muted-foreground italic border-l-2 border-blue-400/40 pl-2">{selectedTpl.body_text}</p>
                                      )}
                                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {Array.from({ length: selectedTpl.parameters_count }, (_, i) => (
                                          <div key={i} className="space-y-1">
                                            <Label className="text-[10px] text-foreground font-mono">{`{{${i + 1}}}`}</Label>
                                            <input
                                              placeholder={i === 0 ? "{nombre}" : "texto fijo"}
                                              className="w-full bg-background border border-blue-400/30 rounded-lg px-2 py-1.5 text-xs text-foreground focus:outline-none focus:border-blue-400"
                                              value={paramVals[i] || ""}
                                              onChange={e => {
                                                const updated = [...Array.from({ length: selectedTpl.parameters_count }, (_, j) => paramVals[j] || "")];
                                                updated[i] = e.target.value;
                                                setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, reengagement_template_params: updated.join(",") } });
                                              }}
                                            />
                                          </div>
                                        ))}
                                      </div>
                                      <p className="text-[10px] text-muted-foreground">Usa <span className="font-mono text-blue-300">{"{nombre}"}</span> para insertar el nombre del lead automáticamente.</p>
                                    </div>
                                  );
                                })()}
                              </div>
                              <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                <p className="text-[10px] text-amber-400">
                                  Solo se contactará a leads que hayan tenido conversación previa con el agente. Se respetará el opt-out y la ventana de 24h de WhatsApp.
                                </p>
                              </div>
                            </div>
                          )}

                          {triggerForm.type === 'notify_salesperson' && (
                            <div className="space-y-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
                              <p className="text-[10px] font-bold text-primary uppercase tracking-wider flex items-center gap-1">
                                <LuUser className="w-3 h-3" />Configuración de notificación al vendedor
                              </p>
                              <div className="space-y-1.5">
                                <Label className="text-xs text-foreground">Teléfono del vendedor (con prefijo, ej: 34612345678)</Label>
                                <input placeholder="34612345678"
                                  className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-foreground focus:outline-none focus:border-primary"
                                  value={triggerForm.config.salesperson_phone}
                                  onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, salesperson_phone: e.target.value } })} />
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-xs text-foreground">Criterios de cualificación</Label>
                                <textarea rows={2}
                                  className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs text-foreground resize-none focus:outline-none focus:border-primary"
                                  value={triggerForm.config.qualification_criteria}
                                  onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, qualification_criteria: e.target.value } })} />
                                <p className="text-[10px] text-muted-foreground">Descripción de cuándo un lead debe notificarse al vendedor</p>
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-xs text-foreground">Plantilla de notificación</Label>
                                <textarea rows={3}
                                  className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-foreground resize-none focus:outline-none focus:border-primary"
                                  value={triggerForm.config.notification_template}
                                  onChange={e => setTriggerForm({ ...triggerForm, config: { ...triggerForm.config, notification_template: e.target.value } })} />
                                <p className="text-[10px] text-muted-foreground">Usa {"{phone}"} y {"{summary}"} como variables</p>
                              </div>
                              <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                <p className="text-[10px] text-amber-400">
                                  La IA evaluará cada mensaje del lead para determinar si cumple los criterios. Solo se notificará una vez por conversación.
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => {
                              setShowTriggerForm(false);
                              setEditingTriggerId(null);
                              setTriggerForm(emptyTriggerForm);
                            }}
                              className="px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:bg-muted/50">Cancelar</button>
                            <button type="button" disabled={triggerSaving} onClick={handleSaveTrigger}
                              className="px-4 py-1.5 rounded-lg text-xs bg-primary text-white hover:bg-primary/90 disabled:opacity-50">
                              {triggerSaving ? "Guardando..." : editingTriggerId ? "Guardar cambios" : "Crear automatización"}
                            </button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </form>
          </div>

          {/* Interactive Preview Sidebar */}
          <aside className="lg:col-span-4 lg:sticky lg:top-24 h-fit">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className="glass-card border-white/10 flex flex-col h-[600px]">
                <CardHeader className="py-4 border-b border-white/5">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <LuZap className="w-4 h-4 text-yellow-400" />
                    Vista Previa en Tiempo Real
                  </CardTitle>
                  <p className="text-[10px] text-muted-foreground">
                    Prueba los cambios de tu prompt al instante
                  </p>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {previewMessages.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-center p-6 opacity-40">
                        <LuMessageSquare className="w-8 h-8 mb-2" />
                        <p className="text-xs">Escribe algo para probar el comportamiento actual</p>
                      </div>
                    ) : (
                      previewMessages.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] p-3 rounded-2xl text-xs ${msg.role === 'user'
                            ? 'bg-primary text-primary-foreground rounded-br-none'
                            : 'bg-muted text-foreground rounded-bl-none'
                            }`}>
                            {msg.content}
                          </div>
                        </div>
                      ))
                    )}
                    {previewSending && (
                      <div className="flex justify-start">
                        <div className="bg-muted p-3 rounded-2xl rounded-bl-none">
                          <div className="flex gap-1">
                            <div className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce" />
                            <div className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce [animation-delay:0.2s]" />
                            <div className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce [animation-delay:0.4s]" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-4 border-t border-white/5 bg-black/20">
                    <form onSubmit={handlePreviewSend} className="flex gap-2">
                      <Input
                        placeholder="Prueba tu agente..."
                        className="h-10 bg-muted/50 border-transparent text-xs rounded-xl"
                        value={previewInput}
                        onChange={(e) => setPreviewInput(e.target.value)}
                      />
                      <Button size="icon" className="h-10 w-10 shrink-0 rounded-xl" disabled={previewSending}>
                        <LuSend className="w-4 h-4" />
                      </Button>
                    </form>
                  </div>
                </CardContent>
              </Card>
              <div className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/10">
                <p className="text-[10px] text-muted-foreground leading-relaxed">
                  <LuSparkles className="inline w-3 h-3 mr-1 text-primary" />
                  <strong>Tip:</strong> Puedes cambiar el "Prompt del Sistema" a la izquierda y volver a preguntar sin necesidad de guardar el agente.
                </p>
              </div>
            </motion.div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default AgentEditorPage;
